// src/modules/screen/Screen.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './styles.css';
import { socket } from '../../config/socket';
import { TVs } from '../services/api';
import bepp from './bepp.mp3';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Fab,
} from '@mui/material';
import CallSection from './components/CallSection';
import WaitingSection from './components/WaitingSection';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://tvs-api.chatup.pe';

const isAbsoluteOrBlob = (u) => /^https?:|^blob:|^data:/.test(String(u));
const toAbsolute = (u) => (isAbsoluteOrBlob(u) ? u : `${API_BASE}${u || ''}`);

const fetchJson = async (url, opts) => {
  const r = await fetch(url, opts);
  if (!r.ok) throw new Error(`HTTP ${r.status} @ ${url}`);
  return r.json();
};

const activeNow = (m, at = new Date()) => {
  const fi = m.fecha_inicio ? new Date(m.fecha_inicio) : null;
  const ff = m.fecha_fin ? new Date(m.fecha_fin) : null;
  const inWindow = (!fi || fi <= at) && (!ff || ff >= at);
  return m.status === 'activo' && inWindow;
};

/* ================== Reloj ================== */
function BigClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const hh = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dd = now.toLocaleDateString([], { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  return (
    <div className="w-screen h-screen bg-black flex flex-col items-center justify-center">
      <div className="text-white text-[15vw] leading-none font-semibold">{hh}</div>
      <div className="text-white/80 text-[3vw] mt-[2vh] capitalize">{dd}</div>
    </div>
  );
}

/* ========== Player HLS-only ========== */
function canPlayNativeHls(videoEl) {
  return !!videoEl?.canPlayType('application/vnd.apple.mpegurl');
}
async function ensureHls() {
  const mod = await import(/* webpackChunkName: "hlsjs" */ 'hls.js');
  return mod?.default || mod;
}

const HlsVideo = React.forwardRef(({ src, soundEnabled, className, onEnded }, ref) => {
  const localRef = useRef(null);
  const videoRef = ref || localRef;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;

    let hls;
    let destroyed = false;

    el.muted = !soundEnabled;

    (async () => {
      try {
        if (canPlayNativeHls(el)) {
          el.src = src;
          await el.play().catch(() => { });
          return;
        }
        const Hls = await ensureHls();
        if (destroyed || !Hls?.isSupported()) {
          el.src = src;
          await el.play().catch(() => { });
          return;
        }

        hls = new Hls({
          lowLatencyMode: false,
          maxBufferLength: 20,
          maxMaxBufferLength: 40,
          backBufferLength: 15,
          capLevelToPlayerSize: true,
        });
        hls.loadSource(src);
        hls.attachMedia(el);
        hls.on(Hls.Events.MANIFEST_PARSED, async () => {
          try { await el.play(); } catch { }
        });
      } catch (e) {
        console.error('HLS setup error', e);
      }
    })();

    return () => {
      destroyed = true;
      if (hls) { try { hls.destroy(); } catch { } }
    };
  }, [src, soundEnabled, videoRef]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted={!soundEnabled}
      controls={false}
      preload="auto"
      crossOrigin="anonymous"
      onEnded={onEnded}
      onLoadedMetadata={() => videoRef.current?.play().catch(() => { })}
      onError={(e) => { console.warn('video error', src, e?.currentTarget?.error); onEnded?.(); }}
      className={className || 'w-full h-full object-contain bg-black'}
    />
  );
});

/* ========== Playlist HLS-only ========== */
function LocalHlsPlaylist({ items = [], soundEnabled = false, className = '' }) {
  const vids = useMemo(() => {
    return (items || [])
      .filter((i) => i?.url_hls)
      .map((i) => ({
        ...i,
        playUrl: toAbsolute(i.url_hls),
      }));
  }, [items]);

  const [idx, setIdx] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    if (vids.length === 0 && idx !== 0) setIdx(0);
    else if (vids.length > 0 && idx >= vids.length) setIdx(0);
  }, [vids.length, idx]);

  const handleEnded = useCallback(() => {
    if (vids.length > 1) {
      // 🎬 varios videos → siguiente
      setIdx((i) => (i + 1) % vids.length);
    } else if (vids.length === 1) {
      // 🔁 un solo video → reiniciar manualmente
      const el = videoRef.current;
      if (el) {
        el.currentTime = 0;
        el.play().catch(() => { });
      }
    }
  }, [vids.length]);

  if (!vids.length) return <div className={className} />;

  const src = vids[idx].playUrl;
  return (
    <HlsVideo
      ref={videoRef}
      src={src}
      soundEnabled={soundEnabled}
      className={className}
      onEnded={handleEnded}
    />
  );
}

/* ========== Ticker y Logo ========== */
function Ticker({ messages = [], durationSec = 30, gap = 48 }) {
  const text = (messages || []).filter(Boolean).join('  •  ');
  if (!text) return null;

  return (
    <div className="ticker">
      <div className="ticker__track" style={{ '--duration': `${durationSec}s`, columnGap: `${gap}px` }}>
        <span className="ticker__chunk">{text}</span>
        <span className="ticker__chunk">{text}</span>
        <span className="ticker__chunk">{text}</span>
      </div>
    </div>
  );
}
function LogoOverlay({ src, size = 96 }) {
  if (!src) return null;
  return (
    <div className="tv-logo" style={{ width: size, height: size }}>
      <img src={src} alt="logo" />
    </div>
  );
}

/* ========== Globals ========== */
const controllers = { pending: null, attending: null };
const inflight = { pending: false, attending: false };

/* ========== Debounce/serialización de colas ========== */
function useDebouncedQueuesRefresher(fetchPending, fetchAttending, beepRef) {
  const inflightRef = useRef(false);
  const tRef = useRef(null);

  const runRefresh = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      await Promise.all([fetchPending(), fetchAttending()]);
      const a = beepRef?.current;
      if (a) {
        a.volume = 1; a.currentTime = 0;
        setTimeout(() => (a.volume = 0), 2000);
      }
    } finally {
      inflightRef.current = false;
    }
  }, [fetchPending, fetchAttending, beepRef]);

  const schedule = useCallback(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(runRefresh, 300);
  }, [runRefresh]);

  return schedule;
}

/* ================== Screen ================== */
export default function Screen({ handleLoading }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const tvParam = (searchParams.get('tv') || '').trim();
  const tvStored = (typeof window !== 'undefined' && localStorage.getItem('tvName')) || '';
  const effectiveTv = (tvParam || tvStored).trim();
  const tvName = (effectiveTv || '').trim();

  const [pendingQueue, setPendingQueue] = useState([]);
  const [attendingQueue, setAttendingQueue] = useState([]);

  const [tvs, setTvs] = useState([]);

  const [readerActive, setReaderActive] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const [openSoundDlg, setOpenSoundDlg] = useState(false);
  const [openTvDlg, setOpenTvDlg] = useState(false);
  const [tvInput, setTvInput] = useState('');

  const [tvDoc, setTvDoc] = useState(null);
  const [groupDoc, setGroupDoc] = useState(null);
  const [playlist, setPlaylist] = useState([]);

  const joinedTvRef = useRef(null);
  const joinedGroupRef = useRef(null);

  const activeMessages = useMemo(
    () => (groupDoc?.mensajes || []).filter((m) => activeNow(m)).map((m) => m.mensaje),
    [groupDoc]
  );

  const handleLoadingRef = useRef(handleLoading);
  useEffect(() => { handleLoadingRef.current = handleLoading; }, [handleLoading]);

  useEffect(() => {
    if (openTvDlg) {
      (async () => {
        try {
          const data = await TVs.getTvs();
          setTvs(Array.isArray(data) ? data : []);
        } catch (e) {
          console.error("Error cargando TVs", e);
          setTvs([]);
        }
      })();
    }
  }, [openTvDlg]);


  /* ====== API colas ====== */
  const fetchPending = useCallback(async () => {
    if (inflight.pending) {
      try { controllers.pending?.abort(); } catch { }
    }
    inflight.pending = true;
    controllers.pending = new AbortController();

    try {
      handleLoadingRef.current?.(true);
      const data = await fetchJson(`${API_BASE}/api/colas/pending`, {
        signal: controllers.pending.signal,
        headers: { 'Cache-Control': 'no-cache' },
      });
      setPendingQueue(data);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('fetchPending', e);
    } finally {
      inflight.pending = false;
      handleLoadingRef.current?.(false);
    }
  }, []);

  const fetchAttending = useCallback(async () => {
    if (inflight.attending) {
      try { controllers.attending?.abort(); } catch { }
    }
    inflight.attending = true;
    controllers.attending = new AbortController();

    try {
      handleLoadingRef.current?.(true);
      const data = await fetchJson(`${API_BASE}/api/colas/attending`, {
        signal: controllers.attending.signal,
        headers: { 'Cache-Control': 'no-cache' },
      });
      data.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
      setAttendingQueue(data);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('fetchAttending', e);
    } finally {
      inflight.attending = false;
      handleLoadingRef.current?.(false);
    }
  }, []);

  const scheduleQueuesRefresh = useDebouncedQueuesRefresher(fetchPending, fetchAttending, null);

  /* ====== TV/Grupo/Playlist ====== */
  const fetchTvByName = useCallback(async (name) => {
    try {
      console.log('aver', API_BASE)
      const tv = await fetchJson(`${API_BASE}/api/pantallas/tv?name=${encodeURIComponent(name)}`);
      if (tv && tv._id) return tv;
      if (Array.isArray(tv)) {
        return tv.find((x) => x.nombre?.toLowerCase() === name.toLowerCase()) || null;
      }
    } catch { }
    const list = await fetchJson(`${API_BASE}/api/pantallas/tv`);
    return list.find((x) => x.nombre?.toLowerCase() === name.toLowerCase()) || null;
  }, []);

  const fetchGroupDoc = useCallback(async (groupId) => {
    const list = await fetchJson(`${API_BASE}/api/pantallas/grupos`);
    return list.find((g) => String(g._id) === String(groupId)) || null;
  }, []);

  const fetchPlaylist = useCallback(async (groupId) => {
    const it = await fetchJson(`${API_BASE}/api/pantallas/grupos/${groupId}/playlist?onlyActive=true`);
    it.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    return it;
  }, []);

  const refreshAll = useCallback(
    async (opts = { skipQueues: false }) => {
      if (!tvName) {
        setTvDoc(null); setGroupDoc(null); setPlaylist([]);
        return;
      }
      const tv = await fetchTvByName(tvName);
      setTvDoc(tv || null);
      if (tv?.grupoId) {
        const [g, pl] = await Promise.all([fetchGroupDoc(tv.grupoId), fetchPlaylist(tv.grupoId)]);
        setGroupDoc(g || null);
        setPlaylist(Array.isArray(pl) ? pl : []);
      } else {
        setGroupDoc(null);
        setPlaylist([]);
      }
      if (tv?.tipo === 'colas' && !opts.skipQueues) {
        scheduleQueuesRefresh();
      }
    },
    [tvName, fetchTvByName, fetchGroupDoc, fetchPlaylist, scheduleQueuesRefresh]
  );

  useEffect(() => {
    if (tvDoc?.tipo === 'colas') {
      scheduleQueuesRefresh();
    } else {
      setPendingQueue([]);
      setAttendingQueue([]);
    }
  }, [tvDoc?.tipo, scheduleQueuesRefresh]);


  const onActivateSound = async () => {
    setSoundEnabled(true);
    try { await document.documentElement.requestFullscreen(); } catch { }
    try {
      const a = new Audio(bepp);
      a.loop = false;
      a.volume = 0;
      await a.play();
      setTimeout(() => a.pause(), 200);
    } catch { }
    setOpenSoundDlg(false);
  };

  useEffect(() => {
    if (!tvParam && tvStored) {
      const next = new URLSearchParams(searchParams);
      next.set('tv', tvStored);
      setSearchParams(next, { replace: true });
      return;
    }
    if (!tvParam && !tvStored) {
      setTvInput('');
      setOpenTvDlg(true);
    }
  }, []); // eslint-disable-line

  const onSaveTvName = () => {
    const name = (tvInput || '').trim();
    if (!name) return;
    try { localStorage.setItem('tvName', name); } catch { }
    const next = new URLSearchParams(searchParams);
    next.set('tv', name);
    setSearchParams(next, { replace: true });
    setOpenTvDlg(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!tvName) { setTvDoc(null); setGroupDoc(null); setPlaylist([]); return; }
        const tv = await fetchTvByName(tvName);
        if (cancelled) return;
        setTvDoc(tv || null);
        if (tv?.grupoId) {
          const [g, pl] = await Promise.all([fetchGroupDoc(tv.grupoId), fetchPlaylist(tv.grupoId)]);
          if (cancelled) return;
          setGroupDoc(g || null);
          setPlaylist(Array.isArray(pl) ? pl : []);
        } else {
          setGroupDoc(null);
          setPlaylist([]);
        }
        if (tv?.tipo === 'colas') scheduleQueuesRefresh();
      } catch (e) {
        console.error(e);
        if (!cancelled) { setTvDoc(null); setGroupDoc(null); setPlaylist([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [tvName, fetchTvByName, fetchGroupDoc, fetchPlaylist, scheduleQueuesRefresh]);

  const items = useMemo(
    () =>
      (playlist || [])
        .filter((v) => !!v.url_hls)
        .map((v) => ({
          url_hls: v.url_hls,
          duracionSeg: v.duracionSeg,
          loop: !!v.loop,
          volumen: Number.isFinite(v.volumen) ? Number(v.volumen) : 1,
        })),
    [playlist]
  );

  // Evita solapar llamadas de colas
  const refreshingRef = useRef(false);

  // Refresco de colas (simple y serializado)
  const refreshQueues = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      const [p, a] = await Promise.all([
        fetchJson(`${API_BASE}/api/colas/pending`),
        fetchJson(`${API_BASE}/api/colas/attending`),
      ]);
      a.sort((x, y) => new Date(y.startTime) - new Date(x.startTime));
      setPendingQueue(p);
      setAttendingQueue(a);
    } catch (e) {
      console.error('refreshQueues', e);
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  // Debounce simple para agrupar ráfagas
  const debounceIdRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (debounceIdRef.current) clearTimeout(debounceIdRef.current);
    debounceIdRef.current = setTimeout(() => {
      refreshQueues();
    }, 300);
  }, [refreshQueues]);

  useEffect(() => {
    try {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('pendingUpdate');
      socket.off('attendingUpdate');

      const onConnect = () => console.log('Socket conectado');
      const onDisconnect = () => console.log('Socket desconectado');
      const onQueuesEvent = () => {
        refreshQueues();
        scheduleRefresh();
      };

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('pendingUpdate', onQueuesEvent);
      socket.on('attendingUpdate', onQueuesEvent);

      // Manejar error si no se conecta
      socket.on('connect_error', (err) => {
        console.error('Error de conexión WebSocket:', err.message);
      });

      return () => {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('pendingUpdate', onQueuesEvent);
        socket.off('attendingUpdate', onQueuesEvent);
        socket.off('connect_error');
      };
    } catch (e) {
      console.error('Fallo al conectar el WebSocket:', e);
    }
  }, []);

  useEffect(() => {
    const tvId = tvDoc?._id;
    const prev = joinedTvRef.current;
    if (prev && prev !== tvId) {
      try { socket.emit('leaveTv', String(prev)); } catch { }
    }
    if (tvId) {
      try { socket.emit('joinTv', String(tvId)); } catch { }
      joinedTvRef.current = tvId;
    }
  }, [tvDoc?._id]);

  useEffect(() => {
    const gid = tvDoc?.grupoId;
    const prev = joinedGroupRef.current;
    if (prev && prev !== gid) {
      try { socket.emit('leaveGroup', String(prev)); } catch { }
    }
    if (gid) {
      try { socket.emit('joinGroup', String(gid)); } catch { }
      joinedGroupRef.current = gid;
    }
  }, [tvDoc?.grupoId]);

  useEffect(() => {
    const onTvUpdated = (payload) => {
      const currentId = tvDoc?._id && String(tvDoc._id);
      if (!payload?.tvId || !currentId || String(payload.tvId) !== currentId) return;
      refreshAll({ skipQueues: true });
    };
    const onGroupUpdated = (payload) => {
      const gid = tvDoc?.grupoId && String(tvDoc.grupoId);
      if (!payload?.groupId || !gid || String(payload.groupId) !== gid) return;
      refreshAll({ skipQueues: true });
    };
    const onTvsIndexChanged = () => { refreshAll({ skipQueues: true }); };

    socket.on('pantallas:tvUpdated', onTvUpdated);
    socket.on('pantallas:grupoUpdated', onGroupUpdated);
    socket.on('pantallas:tvsIndexChanged', onTvsIndexChanged);

    return () => {
      socket.off('pantallas:tvUpdated', onTvUpdated);
      socket.off('pantallas:grupoUpdated', onGroupUpdated);
      socket.off('pantallas:tvsIndexChanged', onTvsIndexChanged);
    };
  }, [tvDoc?._id, tvDoc?.grupoId, refreshAll]);

  const CommonDialogsAndFab = (
    <>
      <Dialog open={openSoundDlg} onClose={() => setOpenSoundDlg(false)}>
        <DialogTitle>Activar sonido</DialogTitle>
        <DialogContent>
          <Button variant="contained" onClick={onActivateSound}>Activar</Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openTvDlg}
        onClose={() => setOpenTvDlg(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0b1220',
            color: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      >
        <DialogTitle>Seleccionar TV</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <div className="flex flex-col gap-2">
            {tvs.map((tv) => (
              <Button
                key={tv._id}
                variant="contained"
                onClick={() => {
                  const name = tv.nombre.trim();
                  try { localStorage.setItem('tvName', name); } catch { }
                  const next = new URLSearchParams(searchParams);
                  next.set('tv', name);
                  setSearchParams(next, { replace: true });
                  setOpenTvDlg(false);
                }}
              >
                {tv.nombre} ({tv.tipo})
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Fab
        size="small"
        aria-label="Cambiar TV"
        onClick={() => { setTvInput(tvName || ''); setOpenTvDlg(true); }}
        style={{ position: 'fixed', right: 12, bottom: 12, zIndex: 50 }}
      >
        TV
      </Fab>
      <Fab
        size="small"
        aria-label="Activar sonido"
        onClick={() => setOpenSoundDlg(true)}
        style={{ position: 'fixed', right: 12, bottom: 70, zIndex: 50 }} // un poco arriba del FAB de TV
      >
        FULL
      </Fab>
    </>
  );

  if (!tvName || !tvDoc?.grupoId) {
    return (
      <>
        <BigClock />
        {CommonDialogsAndFab}
      </>
    );
  }

  if (tvDoc?.tipo === 'publicidad') {
    return (
      <div className="fixed inset-0 bg-black">
        <div className="relative w-full h-full ">
          <LocalHlsPlaylist
            items={items}
            soundEnabled={soundEnabled}
            className="w-full h-full object-contain bg-black"
          />
          <LogoOverlay src={groupDoc?.diseno?.logo} />
          <Ticker messages={activeMessages} />
        </div>
        {CommonDialogsAndFab}
      </div>
    );
  }

  return (
    <div>
      {tvDoc?.tipo === 'colas' && <CallSection attending={attendingQueue} />}
      <div className="queue-screen relative">
        <div className="relative w-full mt-[10px]" style={{ height: '540px' }}>
          <LocalHlsPlaylist
            items={items}
            soundEnabled={soundEnabled}
            className="w-full h-full object-cover bg-black"
          />
          <LogoOverlay src={groupDoc?.diseno?.logo} />
          <Ticker messages={activeMessages} />
        </div>
        <WaitingSection
          pendingQueue={pendingQueue}
          readerActive={readerActive}
          onActivate={() => { setReaderActive(true); }}
        />
      </div>
      {CommonDialogsAndFab}
    </div>
  );
}
